import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Icon,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
import React, { FunctionComponent, JSX, KeyboardEvent, useEffect, useState } from 'react';

import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { IWithStylesProps } from '~/src/dash/types/props';
import { containAllItems, containAtLeastOneItems } from '~/src/dash/utils/basic';
import { decorateV2 } from '~/src/dash/utils/decorator';
import { errorColor } from '~/src/dash/utils/material-dashboard-react';
import {
  auctionGroupByListExtendedOnly,
  auctionGroupByListFast,
  auctionGroupByListFastOnly,
  auctionGroupByListWithAdomain,
  auctionGroupByListWithDemandPath,
  buyerGroupByListExtendedOnly,
  buyerGroupByListFast,
  buyerGroupByListFastOnly,
  buyerGroupByListWithAdomain,
  buyerGroupByListWithDemandPath,
} from '~/src/dash/utils/report-fields';
import { isSamsungNAPublisher } from '~/src/dash/utils/ui';
import { ct } from '~/src/dash/publicaUI';
// This is an older component, you should use MaterialSearchDropdownMulti if possible

export interface IMultiSelectOption {
  id: number;
  key?: string;
  text: string;
  selected: boolean;
  isSubCategory: boolean;
  categoryName?: string;
  locked?: boolean;
}

export interface ISubCategoryItem {
  title: string;
  matchRegex: string | RegExp;
}

export interface IMultiSelect extends IWithStylesProps {
  items: Array<Partial<IMultiSelectOption>>;
  disableItems?: string[];
  values: string[]; // Holds the currently selected values in the dropdown and also used to populate default values
  label: string | React.ReactNode;
  helperText?: string | JSX.Element;
  filterHelperText?: string;
  name: string;
  onChange: (name: string, value: string[]) => void;
  search?: boolean;
  className?: string;
  style?: { [key: string]: any };
  maxSearchResultsCount?: number;
  error?: boolean;
  subCategories?: ISubCategoryItem[];
  subCategoriesDefault?: boolean;
  textAdornment?: string;
  disabled?: boolean;
  showNumber?: boolean; // if true, displays the number of options selected / 'all' while menu is closed
  textfieldClass: string;
  selectFontSize?: number;
  campaignFields?: string[];
  disableCampaignFields?: boolean;
  dealFields?: string[];
  disableDealFields?: boolean;
  woyFields?: string[];
  disableNonWOYFields?: boolean;
  disableWOY?: boolean;
  disableClear?: boolean;
  graphID?: string;
  // Enable Select All checkbox
  // default=false
  selectAll?: boolean;
  publisherID?: string; // for publisher specific tooltip message
}

interface IMultiSelectList {
  disabledItems: string[];
  allItems: Array<Partial<IMultiSelectOption>>;
  categories?: ISubCategoryItem[];
  filteredItems: Array<Partial<IMultiSelectOption>>;
  handleItemClick: (id: number) => void;
  isSubOpen: Map<string, boolean>;
  updateSubOpen: (subName: string) => void;
  selectedVals: string[];
  graphID?: string;
  disableWOY?: boolean;
  campaignFields?: string[];
  disableCampaignFields?: boolean;
  dealFields?: string[];
  disableDealFields?: boolean;
  woyFields?: string[];
  disableNonWOYFields?: boolean;
  publisherID?: string;
}

const DEFAULT_MAX_SEARCH_RESULTS = Infinity;

const createListOfCategories = function (
  items: Array<Partial<IMultiSelectOption>>,
  categories?: ISubCategoryItem[],
  selectedVals?: string[],
): Array<Partial<IMultiSelectOption>> {
  // Only generate subCategories when categories are provided in the first place
  if (!categories) {
    // Make sure the necessary fields are added
    for (let i = 0; i < items.length; i++) {
      items[i].id = i;
      items[i].selected = !!selectedVals && selectedVals.includes(items[i].key || '');
      items[i].isSubCategory = false;
    }
    return items;
  }

  // Create a map of all categories
  const categoryMap = new Map<string, Array<Partial<IMultiSelectOption>>>();
  categories.forEach((category: ISubCategoryItem) => {
    categoryMap.set(category.title, []);
  });

  // Add the appropriate item into the appropriate category
  for (const item of items) {
    for (const category of categories) {
      const c = categoryMap.get(category.title);
      if (c && item.text !== undefined && item.text.search(category.matchRegex) !== -1) {
        c.push(item);
        break;
      }
    }
  }

  let idOffset = 0;
  const newItems: IMultiSelectOption[] = [];
  categories.forEach((category: ISubCategoryItem) => {
    const c = categoryMap.get(category.title);
    if (c && c.length >= 1) {
      // Add the category name
      newItems.push({
        id: idOffset,
        text: category.title,
        isSubCategory: true,
        categoryName: category.title,
        selected: false,
      });
      idOffset += 1;

      // For each category, add the sub items inside it
      for (const item of c) {
        if (item.text === undefined) {
          item.text = '';
        }
        newItems.push({
          id: idOffset,
          key: item.key,
          text: item.text,
          isSubCategory: false,
          categoryName: category.title,
          selected: !!selectedVals && selectedVals.includes(item.key || ''),
        });
        idOffset += 1;
      }
    }
  });

  return newItems;
};

const disabledTTDFields = [
  'day',
  'currency_type',
  'timezone',
  'market_type',
  'impressions',
  'media_cost',
];

const getToolTipMsg = (
  key: string,
  groupByOptions: string[],
  graphID?: string,
  dealFields?: string[],
  disableDealFields?: boolean,
  publisherID?: string,
): string => {
  if (graphID === '40') {
    if (containAtLeastOneItems(auctionGroupByListExtendedOnly, groupByOptions)) {
      if (auctionGroupByListExtendedOnly.includes(key) && groupByOptions.includes(key)) {
        return "You have selected group by options that's only supported by scheduling report";
      }
    } else if (
      !containAllItems(auctionGroupByListFast, groupByOptions) &&
      !containAllItems(auctionGroupByListWithAdomain, groupByOptions) &&
      !containAllItems(auctionGroupByListWithDemandPath, groupByOptions)
    ) {
      if (
        auctionGroupByListFastOnly.concat(['adomain', 'demand_path']).includes(key) &&
        groupByOptions.includes(key)
      ) {
        return "You have selected combinations of that's only supported by scheduling report";
      }
    }
  }
  if (graphID === '41') {
    if (containAtLeastOneItems(buyerGroupByListExtendedOnly, groupByOptions)) {
      if (buyerGroupByListExtendedOnly.includes(key) && groupByOptions.includes(key)) {
        return "You have selected group by options that's only supported by scheduling report";
      }
    } else if (
      !containAllItems(buyerGroupByListFast, groupByOptions) &&
      !containAllItems(buyerGroupByListWithAdomain, groupByOptions) &&
      !containAllItems(buyerGroupByListWithDemandPath, groupByOptions)
    ) {
      if (
        buyerGroupByListFastOnly.concat(['adomain', 'demand_path']).includes(key) &&
        groupByOptions.includes(key)
      ) {
        return "You have selected combinations of that's only supported by scheduling report";
      }
    }
  }
  if ((graphID === '40' || graphID === '41') && groupByOptions.length > 6) {
    if (groupByOptions.includes(key)) {
      return 'You have selected more than 6 group by options';
    }
  }
  if (graphID === '65') {
    if (disableDealFields && dealFields && !dealFields.includes(key)) {
      return 'Only these fields are available with the bidder group by options';
    }
  }
  if (graphID === '78') {
    if (key === 'render_rate') {
      return 'This will include results where the render rate is less than or equal to 10%';
    }
    if (key === 'bid_wons') {
      if (publisherID && isSamsungNAPublisher(publisherID)) {
        return 'This will include results where bid won is greater than or equal to 1000';
      } else {
        return 'This will include results where bid won is greater than or equal to 200';
      }
    }
  }
  return '';
};

const MenuItemList = ({
  allItems,
  categories,
  filteredItems,
  handleItemClick,
  disabledItems,
  isSubOpen,
  updateSubOpen,
  campaignFields,
  disableCampaignFields,
  woyFields,
  disableNonWOYFields,
  disableWOY,
  dealFields,
  disableDealFields,
  selectedVals,
  graphID,
  publisherID,
}: IMultiSelectList) => {
  return (
    <React.Fragment>
      {filteredItems.map(
        ({ id, key, text, isSubCategory, categoryName, locked }: IMultiSelectOption) => {
          // isSubCategory=false refers to the headers under the menu (e.g. Time Range, Demand, etc.)
          if (isSubCategory) {
            const itemIsDisabled =
              !!key &&
              ((campaignFields && disableCampaignFields && campaignFields.includes(key)) ||
                (woyFields && disableNonWOYFields && !woyFields.includes(key)) ||
                (disableWOY && key === 'week_of_year'));

            return (
              <MenuItem disabled={itemIsDisabled} key={'t_' + text}>
                <ListItemText
                  primary={text}
                  disableTypography={true}
                  style={{ fontWeight: 'bold' }}
                  onClick={() => {
                    updateSubOpen(categoryName || '');
                  }}
                />
                <Checkbox
                  checked={isSubOpen.get(categoryName || '')}
                  icon={<ExpandMoreIcon />}
                  checkedIcon={<ExpandLessIcon />}
                  onChange={() => updateSubOpen(categoryName || '')}
                />
              </MenuItem>
            );
          } else if (key && (!categories || isSubOpen.get(categoryName || ''))) {
            const tooltipMsg = getToolTipMsg(
              key,
              selectedVals,
              graphID,
              dealFields,
              disableDealFields,
              publisherID,
            );

            const itemIsDisabled =
              (!!key &&
                (disabledItems.includes(key) ||
                  (campaignFields && disableCampaignFields && campaignFields.includes(key)) ||
                  (dealFields && disableDealFields && dealFields.includes(key)) ||
                  (woyFields && disableNonWOYFields && !woyFields.includes(key)) ||
                  (disableWOY && key === 'week_of_year'))) ||
              locked;

            return (
              <MenuItem
                key={'i_' + key}
                value={key}
                disabled={itemIsDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemClick(id);
                }}
              >
                <Checkbox checked={allItems[id].selected} />
                <ListItemText primary={text} />
                {!!tooltipMsg && tooltipMsg !== '' && (
                  <Tooltip title={tooltipMsg}>
                    <InfoIcon style={{ width: '1rem', color: '#929599' }} />
                  </Tooltip>
                )}
              </MenuItem>
            );
          } else {
            return <React.Fragment />;
          }
        },
      )}
    </React.Fragment>
  );
};

const _CategoryMultiSelect: FunctionComponent<Partial<IMultiSelect>> = function ({
  items,
  disableItems,
  values,
  label,
  helperText,
  filterHelperText,
  name,
  onChange,
  search,
  classes,
  className,
  style,
  maxSearchResultsCount,
  error,
  subCategories,
  textAdornment,
  disabled,
  subCategoriesDefault,
  textfieldClass,
  showNumber,
  selectFontSize,
  disableCampaignFields,
  campaignFields,
  dealFields,
  disableDealFields,
  woyFields,
  disableNonWOYFields,
  disableWOY,
  disableClear,
  graphID,
  selectAll,
  publisherID,
}: IMultiSelect) {
  if (textAdornment === undefined) {
    textAdornment = '';
  }

  const [filterText, setFilterText] = useState('');
  const [selectedVals, setSelectedVals] = useState(values || ([] as string[]));
  const [menuItems, setMenuItems] = useState(
    createListOfCategories(items, subCategories, selectedVals),
  );
  const [isSubOpen, setIsSubOpen] = useState(() => {
    const subMap = new Map<string, boolean>();
    if (subCategories) {
      // Set initial state
      subCategories!.forEach((category: ISubCategoryItem) => {
        subMap.set(category.title, subCategoriesDefault || true);
      });
    }
    return subMap;
  });

  useEffect(() => {
    // When the parent component updates the items prop, then update
    // the local menuItems state variable.
    const newItems = createListOfCategories(items, subCategories, values);
    setMenuItems(newItems);
    setSelectedVals(
      // Set the remaining selected values in case some items that were
      // selected were to be removed
      newItems
        .filter((item: IMultiSelectOption) => item.selected)
        .map((item: IMultiSelectOption) => item.key || ''),
    );
  }, [items, values]);

  const updateSubOpen = function (subName: string) {
    const subMap = new Map(isSubOpen);
    subMap.set(subName, !subMap.get(subName));
    setIsSubOpen(subMap);
  };

  const updateSelectVals = (items: Array<Partial<IMultiSelectOption>>) => {
    setMenuItems(items);

    // Get the strings of all the selected items
    const newSelectVal = [] as string[];
    for (const item of items) {
      if (item.key && item.selected && !item.isSubCategory) {
        newSelectVal.push(item.key);
      }
    }
    setSelectedVals(newSelectVal);
    onChange(name, newSelectVal);
  };

  const handleItemClick = (id: number): void => {
    if (menuItems[id].isSubCategory) {
      return;
    }
    const newItems = cloneDeep(menuItems);
    newItems[id].selected = !newItems[id].selected;
    updateSelectVals(newItems);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItems = cloneDeep(menuItems);
    for (let i = 0; i < newItems.length; i++) {
      newItems[i].selected = event.target.checked;
    }
    updateSelectVals(newItems);
  };

  const checkSelectAllStatus = (): boolean => {
    const newItems = cloneDeep(menuItems);
    for (let i = 0; i < newItems.length; i++) {
      if (!newItems[i].selected) {
        return false;
      }
    }
    return true;
  };

  const maxSearchLength =
    search && filterText.length > 0
      ? maxSearchResultsCount || DEFAULT_MAX_SEARCH_RESULTS
      : items.length;
  const filteredItems =
    filterText.length === 0
      ? menuItems
      : menuItems
          .filter(({ text }: IMultiSelectOption): boolean => {
            return text.toLowerCase().includes(filterText.toLowerCase());
          })
          .slice(0, maxSearchLength);

  // if a GroupBy time unit has been selected, other time units should be disabled
  let disabledItems = [] as string[];
  if (graphID === '82') {
    disabledItems = disabledTTDFields;
  }

  if (name === 'GroupByOptions') {
    if (graphID !== '82') {
      if (selectedVals.includes('hour')) {
        disabledItems = ['day', 'month', 'week_of_year'];
      } else if (selectedVals.includes('day')) {
        disabledItems = ['hour', 'month', 'week_of_year'];
      } else if (selectedVals.includes('month')) {
        disabledItems = ['hour', 'day', 'week_of_year'];
      } else if (selectedVals.includes('week_of_year')) {
        disabledItems = ['hour', 'day', 'month'];
      }
    }

    if (selectedVals.includes('sov_inventory_type')) {
      disabledItems.push('is_fallback_response');
    } else if (selectedVals.includes('is_fallback_response')) {
      disabledItems.push('sov_inventory_type');
    }

    if (disableItems) {
      disableItems.forEach((item) => {
        disabledItems.push(item);
      });
    }
  }

  const clearButtonOnClick = () => {
    onChange(name, []);
    setSelectedVals([]);
    if (graphID === '82') {
      if (name === 'GroupByOptions') {
        const vals = ['day', 'currency_type', 'timezone', 'market_type'];
        onChange(name, vals);
        setSelectedVals(vals);
      }
      if (name === 'Metrics') {
        const vals = ['impressions', 'media_cost'];
        onChange(name, vals);
        setSelectedVals(vals);
      }
    }

    const newMenuItems = cloneDeep(menuItems);
    for (let i = 0; i < newMenuItems.length; i++) {
      if (graphID === '82' && isDisabledTTDItem(newMenuItems[i].key)) {
        newMenuItems[i].selected = true;
      } else {
        newMenuItems[i].selected = false;
      }
    }
    setMenuItems(newMenuItems);
  };

  const isDisabledTTDItem = (key: string | undefined) => {
    let isDisabled = false;
    if (key) {
      disabledTTDFields.forEach((val) => {
        if (key === val) {
          isDisabled = true;
        }
      });
    }

    return isDisabled;
  };

  return (
    <div className={classNames(classes.container, className)} style={style}>
      <FormControl classes={{ root: classes.formControl }}>
        {label !== '' && (
          <InputLabel
            htmlFor={'select-multiple-checkbox-' + name}
            style={{ fontSize: selectFontSize }}
            classes={{ root: classes.formText }}
          >
            {label}
          </InputLabel>
        )}
        <Select
          multiple
          displayEmpty
          fullWidth
          disabled={!!disabled}
          name={name}
          value={selectedVals}
          key={'select_' + filterHelperText}
          style={selectFontSize ? { fontSize: `${selectFontSize}px` } : {}}
          onChange={() => onChange(name, selectedVals)}
          onOpen={() => setFilterText('')}
          input={<Input id={'select-multiple-checkbox-' + name} />}
          renderValue={(selected: string[]) => {
            const removeAllFromSelected = selected.filter((s: string) => s !== 'all');
            // Show the number of items selected or all if not 1 item
            if (showNumber && removeAllFromSelected.length !== 1) {
              return `${
                removeAllFromSelected.length === items.length
                  ? 'All'
                  : `${removeAllFromSelected.length}`
              } ${label} Selected`;
            }

            return menuItems
              .filter(({ key }: IMultiSelectOption) => {
                return key && selected.indexOf(key) > -1;
              })
              .map(({ text }: IMultiSelectOption) => {
                return textAdornment + text;
              })
              .join(', ');
          }}
        >
          {search && (
            <MenuItem>
              <TextField
                fullWidth
                autoFocus
                value={filterText}
                onChange={({ target }: React.ChangeEvent<HTMLElement>) => {
                  setFilterText((target as HTMLSelectElement).value);
                }}
                placeholder={filterHelperText}
                InputProps={{
                  classes: {
                    input: textfieldClass ? textfieldClass : '',
                  },
                }}
                key={'search_' + filterHelperText}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  e.stopPropagation();
                }}
              />
              <IconButton
                color='primary'
                classes={{ root: classes.iconButton }}
                onClick={() => {
                  setFilterText('');
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </MenuItem>
          )}

          {selectAll && (
            <MenuItem>
              <FormControlLabel
                control={<Checkbox onChange={handleSelectAll} checked={checkSelectAllStatus()} />}
                label='Select All'
              />
            </MenuItem>
          )}

          <MenuItemList
            allItems={menuItems}
            categories={subCategories}
            filteredItems={filteredItems}
            handleItemClick={handleItemClick}
            disabledItems={disabledItems}
            isSubOpen={isSubOpen}
            updateSubOpen={updateSubOpen}
            campaignFields={campaignFields}
            disableCampaignFields={disableCampaignFields}
            woyFields={woyFields}
            disableNonWOYFields={disableNonWOYFields}
            disableWOY={disableWOY}
            dealFields={dealFields}
            disableDealFields={disableDealFields}
            selectedVals={selectedVals}
            graphID={graphID}
            publisherID={publisherID}
          />

          {search && filteredItems.length !== menuItems.length && (
            <MenuItem key={'results_' + filterHelperText} disabled>
              <ListItemText
                primary={`Showing ${filteredItems.length} of ${menuItems.length} results...`}
              />
            </MenuItem>
          )}
        </Select>
        {helperText && (
          <FormHelperText className={error ? classes.errorClass : ''}>{helperText}</FormHelperText>
        )}
      </FormControl>
      {!disableClear && (
        <Button
          style={
            disabled === undefined || !disabled
              ? { color: getColorTheme('secondaryButtonText') }
              : { color: getColorTheme('secondaryButtonDisabled') }
          }
          classes={{ root: classes.button }}
          disabled={!!disabled}
          onClick={clearButtonOnClick}
        >
          {ct('Clear')}
        </Button>
      )}
    </div>
  );
};

export const CategoryMultiSelect = decorateV2<Partial<IMultiSelect>>(
  {
    styles: () => ({
      container: {
        display: 'flex',
        alignItems: 'baseline',
      },
      formControl: {
        flexGrow: '1',
      },
      formText: {
        '&.Mui-focused': {
          color: '#354975',
        },
        color: '#000000',
      },
      errorClass: {
        color: errorColor.color,
      },
      button: {
        cursor: 'pointer',
        marginTop: '16px',
      },
    }),
  },
  _CategoryMultiSelect,
);
